import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import { useSignal } from '@backstage/plugin-signals-react';
import type { Notification } from '@backstage/plugin-notifications-common';
import { Button, Link as BuiLink, Skeleton, Text } from '@backstage/ui';
import { RiNotification3Line } from '@remixicon/react';
import { timeAgo } from '../../home/needsYou/time';

const PANEL_LIMIT = 8;
/** Enough to mark everything read in one request. */
const MARK_ALL_LIMIT = 200;

const severityLabel: Record<string, string | undefined> = {
  critical: 'Critical',
  high: 'High',
};

/**
 * The unread count, refreshed when the notifications backend signals a new
 * notification or a read change, and when the window regains focus (signals
 * can drop while a laptop sleeps). A failed refresh is shown on the bell
 * rather than posted as an error: it runs on every signal and focus, so a
 * backend outage would otherwise flood the error display.
 */
const useUnreadCount = () => {
  const api = useApi(notificationsApiRef);
  const { lastSignal } = useSignal('notifications');
  const [unread, setUnread] = useState(0);
  const [failed, setFailed] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => {
    setVersion(v => v + 1);
    api.getStatus().then(
      status => {
        setUnread(status.unread);
        setFailed(false);
      },
      () => setFailed(true),
    );
  }, [api]);
  useEffect(refresh, [refresh, lastSignal]);
  useEffect(() => {
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refresh]);
  return { unread, failed, version, refresh };
};

type PanelProps = {
  id: string;
  anchor: HTMLElement;
  left: number;
  unread: number;
  /** Changes on every notifications signal and read change: reloads the list. */
  version: number;
  onClose: (restoreFocus: boolean) => void;
  onOpenNotification: (notification: Notification) => void;
  onMarkAllRead: () => void;
};

const NotificationsPanel = ({
  id,
  anchor,
  left,
  unread,
  version,
  onClose,
  onOpenNotification,
  onMarkAllRead,
}: PanelProps) => {
  const api = useApi(notificationsApiRef);
  const ref = useRef<HTMLDivElement>(null);
  const [bottom, setBottom] = useState(8);
  const [state, setState] = useState<{
    notifications?: Notification[];
    error?: Error;
  }>({});

  useEffect(() => {
    let current = true;
    api
      .getNotifications({
        limit: PANEL_LIMIT,
        sort: 'created',
        sortOrder: 'desc',
      })
      .then(
        result => current && setState({ notifications: result.notifications }),
        error =>
          current &&
          setState({
            error: error instanceof Error ? error : new Error(String(error)),
          }),
      );
    return () => {
      current = false;
    };
  }, [api, version]);

  // Open upwards from the bell, like the More panel from its row.
  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 0;
    const fromRow = window.innerHeight - anchor.getBoundingClientRect().bottom;
    setBottom(Math.max(8, Math.min(fromRow, window.innerHeight - height - 8)));
  }, [anchor, state]);

  useEffect(() => {
    const panel = ref.current;
    panel?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose(true);
      }
    };
    panel?.addEventListener('keydown', onKeyDown);
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!ref.current?.contains(target) && !anchor.contains(target)) {
        onClose(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      panel?.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [anchor, onClose]);

  const { notifications, error } = state;

  return createPortal(
    <div
      ref={ref}
      id={id}
      className="make-notifications"
      role="dialog"
      aria-label="Notifications"
      tabIndex={-1}
      style={{ left, bottom }}
    >
      <div className="make-notifications__header">
        <Text as="h2" variant="title-x-small" weight="bold">
          Notifications
        </Text>
        <Button
          size="small"
          variant="tertiary"
          isDisabled={unread === 0}
          onPress={onMarkAllRead}
        >
          Mark all as read
        </Button>
      </div>
      <div className="make-notifications__body">
        {!notifications && !error && (
          <div role="progressbar" aria-label="Loading">
            {[0, 1, 2].map(i => (
              <Skeleton key={i} width="100%" height={52} rounded />
            ))}
          </div>
        )}
        {error && (
          <Text as="p" variant="body-small" color="secondary">
            Couldn't load your notifications: {error.message}
          </Text>
        )}
        {notifications?.length === 0 && (
          <Text as="p" variant="body-small" color="secondary">
            You're all caught up.
          </Text>
        )}
        {notifications && notifications.length > 0 && (
          <ul aria-label="Latest notifications">
            {notifications.map(notification => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onOpen={() => onOpenNotification(notification)}
              />
            ))}
          </ul>
        )}
      </div>
      <div className="make-notifications__footer">
        <BuiLink
          href="/notifications"
          variant="body-small"
          weight="bold"
          onPress={() => onClose(false)}
        >
          All notifications
        </BuiLink>
      </div>
    </div>,
    document.body,
  );
};

const NotificationRow = ({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: () => void;
}) => {
  const { title, description, link, severity, topic } = notification.payload;
  const isUnread = !notification.read;
  const meta = [
    severityLabel[severity ?? ''],
    topic,
    timeAgo(String(notification.created)),
  ]
    .filter(Boolean)
    .join(' · ');
  const content = (
    <>
      <span
        className="make-notifications__dot"
        data-severity={severity ?? 'normal'}
        aria-hidden
      />
      {/* The spaces between the parts are invisible in the flex column but
      keep the link's accessible name readable. */}
      <span className="make-notifications__text">
        <span className="make-notifications__title">
          {isUnread && (
            <>
              <span className="make-notifications__sr">Unread:</span>{' '}
            </>
          )}
          {title}
        </span>{' '}
        {description && (
          <>
            <span className="make-notifications__description">
              {description}
            </span>{' '}
          </>
        )}
        <span className="make-notifications__meta">{meta}</span>
      </span>
    </>
  );
  const href = link ?? '/notifications';
  return (
    <li>
      {href.startsWith('/') ? (
        <Link
          className="make-notifications__row"
          data-unread={isUnread || undefined}
          to={href}
          onClick={onOpen}
        >
          {content}
        </Link>
      ) : (
        <a
          className="make-notifications__row"
          data-unread={isUnread || undefined}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onOpen}
        >
          {content}
        </a>
      )}
    </li>
  );
};

type SidebarNotificationsProps = {
  id: string;
  /** Right edge of the sidebar plus a gap, where the panel opens. */
  panelLeft: () => number;
  /** Closes the sidebar's other popups. */
  onPointerEnter: () => void;
  onOpen: () => void;
  onOpenNotification: (notification: Notification) => void;
};

/**
 * The bell in the sidebar footer: an unread count that updates live, and a
 * panel with the latest notifications. The full list is the notifications
 * plugin's page at /notifications.
 */
export const SidebarNotifications = ({
  id,
  panelLeft,
  onPointerEnter,
  onOpen,
  onOpenNotification,
}: SidebarNotificationsProps) => {
  const api = useApi(notificationsApiRef);
  const errorApi = useApi(errorApiRef);
  const { unread, failed, version, refresh } = useUnreadCount();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  const markRead = (ids: string[]) =>
    api
      .updateNotifications({ ids, read: true })
      .then(refresh)
      .catch(error =>
        errorApi.post(
          new Error(`Couldn't mark notifications as read: ${error}`),
        ),
      );

  let label = 'Notifications';
  if (failed) label = "Notifications, couldn't check for new ones";
  else if (unread > 0) label = `Notifications, ${unread} unread`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="make-sidebar__row make-sidebar__bell"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        data-open={open || undefined}
        onPointerEnter={onPointerEnter}
        onClick={() => {
          if (!open) onOpen();
          setOpen(!open);
        }}
      >
        <RiNotification3Line size={20} aria-hidden />
        {failed ? (
          <span
            className="make-sidebar__badge make-sidebar__badge--warning"
            aria-hidden
          >
            !
          </span>
        ) : (
          unread > 0 && (
            <span className="make-sidebar__badge" aria-hidden>
              {unread > 99 ? '99+' : unread}
            </span>
          )
        )}
        <span className="make-sidebar__short" aria-hidden="true">
          Inbox
        </span>
      </button>
      {open && buttonRef.current && (
        <NotificationsPanel
          id={id}
          anchor={buttonRef.current}
          left={panelLeft()}
          unread={unread}
          version={version}
          onClose={close}
          onOpenNotification={notification => {
            onOpenNotification(notification);
            if (!notification.read) markRead([notification.id]);
            close(false);
          }}
          onMarkAllRead={() =>
            api
              .getNotifications({ read: false, limit: MARK_ALL_LIMIT })
              .then(result =>
                markRead(result.notifications.map(item => item.id)),
              )
              .catch(error =>
                errorApi.post(
                  new Error(`Couldn't mark notifications as read: ${error}`),
                ),
              )
          }
        />
      )}
    </>
  );
};
